/*
  This file is part of the project: "Digiziege"
  An app for animal-welfare-oriented goat husbandry and contactless weight estimation.
  GitLab: https://gitlab.rlp.net/proj-wise2526-digiziege/digiziege

  Copyright (c) 2026 Digiziege Team TH-Bingen

  Description:
  Companion app for welfare-oriented goat husbandry featuring contactless weight estimation via camera,
  master data management and health tracking.
  Built as a PWA with a local-first approach to ensure data privacy.

  LICENSE INFORMATION:
  This program is free software: you can redistribute it and/or modify it under the terms of the
  GNU Affero General Public License as published by the Free Software Foundation,
  either version 3 of the License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
  without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
  See the GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License along with this program.
  If not, see <https://www.gnu.org/licenses/>.
*/

/**
 * WeightPipeline.js
 *
 * Orchestrates the complete ML pipeline for weight estimation
 * Based on thesis weight-prediction.ts WeightPredictor
 *
 * Complete Pipeline:
 * 1. Camera/Gallery -> Blob
 * 2. TensorFlow.js Segmentation -> Mask (tf.Tensor2D)
 * 3. Measurement Extraction -> Pixel measurements
 * 4. EXIF + Calibration -> CM measurements
 * 5. Weight Estimation -> Final weight
 */

import { SegmentationService } from './SegmentationService.js'
import { MeasurementService } from './MeasurementService.js'
import { ExifService } from './ExifService.js'
import { CalibrationService } from './CalibrationService.js'
import { WeightEstimatorService } from './WeightEstimatorService.js'
import {
  getDefaultFocalLength,
  getDefaultFocal35mm,
} from '@/utils/ml/useFocalLength.js'
import focalLengthPresetsData from '@/constants/focalLengthPresets.json'

/**
 * Main orchestrator for the ML weight estimation pipeline
 * Based on thesis weight-prediction.ts WeightPredictor class
 */
export class WeightPipeline {
  constructor() {
    // Initialize all services
    this.segmentationService = new SegmentationService()
    this.measurementService = new MeasurementService()
    this.exifService = new ExifService()
    this.calibrationService = new CalibrationService()
    this.weightEstimatorService = new WeightEstimatorService()

    this.isInitialized = false
  }

  /**
   * Load and prepare all ML models
   * Initializes segmentation, measurement, calibration, and weight estimation
   *
   * @param {Function} progressCallback - Optional progress updates with stage and percentage
   */
  async initialize(progressCallback) {
    if (this.isInitialized) {
      return
    }

    try {
      // Initialize TensorFlow.js segmentation model
      if (progressCallback) {
        progressCallback({
          stage: 'segmentation',
          progress: 0.1,
          messageKey: 'ml.pipeline.loadingSegmentationModel',
        })
      }
      await this.segmentationService.initialize()

      // Initialize weight estimator
      if (progressCallback) {
        progressCallback({
          stage: 'weight-estimator',
          progress: 0.8,
          messageKey: 'ml.pipeline.initializingWeightEstimator',
        })
      }
      await this.weightEstimatorService.initialize()

      // Warmup
      await this.weightEstimatorService.warmup()

      if (progressCallback) {
        progressCallback({
          stage: 'complete',
          progress: 1.0,
          messageKey: 'ml.pipeline.pipelineReady',
        })
      }

      this.isInitialized = true
      console.info('[WeightPipeline] Initialized successfully')
    } catch (error) {
      console.error('[WeightPipeline] Initialization failed:', error)
      throw new Error(`Pipeline initialization failed: ${error.message}`)
    }
  }

  /**
   * Complete ML pipeline: detect goat, measure, calibrate, estimate weight
   *
   * Coordinate Mapping Strategy:
   * ML runs on the full original photo (scaled to 640px) to maximize accuracy.
   * UI displays a square-cropped version of the photo.
   * Segmentation data (maskData/boundingBox) is returned in the original photo space.
   */
  async estimateWeight(input, progressCallback) {
    if (!this.isInitialized) {
      await this.initialize(progressCallback)
    }

    const startTime = performance.now()
    const direction = input.direction || 'left'
    const viewingAngle = input.viewingAngle || 20 // Default from thesis

    try {
      // Stage 1: EXIF Extraction (Retrieve camera hardware metadata early for orientation)
      if (progressCallback) {
        progressCallback({
          stage: 'exif',
          progress: 0.1,
          messageKey: 'ml.pipeline.readingCameraData',
        })
      }
      const exifSource = input.exifSource || input.image
      const exifData = await this.exifService.extractExifData(exifSource)

      // Merge with live stream metadata if EXIF is missing from Blob (e.g. canvas capture)
      if (input.metadata) {
        if (!exifData.focalLength && input.metadata.focalLength) {
          exifData.focalLength = input.metadata.focalLength
          exifData.hasFocalLength = true
        }
        if (!exifData.focal35mm && input.metadata.focal35mm) {
          exifData.focalLengthIn35mm = input.metadata.focal35mm
          exifData.focal35mm = input.metadata.focal35mm
        }

        // try to match 35mm from presets, if only physical focal length from stream available
        if (
          exifData.focalLength &&
          !exifData.focal35mm &&
          !exifData.focalLengthIn35mm
        ) {
          const matchingPreset = focalLengthPresetsData.find(
            (p) => Math.abs(p.focalLength - exifData.focalLength) < 0.05
          )
          if (matchingPreset) {
            exifData.focalLengthIn35mm = matchingPreset.focal35mm
            exifData.focal35mm = matchingPreset.focal35mm
            console.info(
              '[WeightPipeline] Matched 35mm equivalent from stream focal length:',
              exifData.focal35mm
            )
          }
        }
      }

      // Stage 2: Segmentation (Goat detection and mask generation)
      if (progressCallback) {
        progressCallback({
          stage: 'segmentation',
          progress: 0.2,
          messageKey: 'ml.pipeline.detectingGoat',
        })
      }
      const segmentation = await this.runSegmentation(
        input.image,
        input.imageCanvas,
        input.resultCanvas,
        // Callers may provide a different orientation object for segmentation
        // (e.g. when the ML image pixels were already orientation-baked).
        input.segmentationExifData || exifData
      )

      if (!segmentation.mask || !segmentation.boundingBox) {
        throw new Error('No goat detected in image')
      }

      // Stage 3: Measurement (Extract physical dimensions in pixels from the mask)
      if (progressCallback) {
        progressCallback({
          stage: 'measurement',
          progress: 0.4,
          messageKey: 'ml.pipeline.extractingMeasurements',
        })
      }
      const pixelMeasurements = await this.runMeasurement(
        segmentation.mask,
        segmentation.boundingBox,
        direction,
        input.resultCanvas
      )

      // Stage 4: Calibration (Convert pixels to cm using pinhole camera model)
      if (progressCallback) {
        progressCallback({
          stage: 'calibration',
          progress: 0.6,
          messageKey: 'ml.pipeline.calibratingMeasurements',
        })
      }

      // Precedence: 1. Manual user adjustment, 2. EXIF data, 3. App defaults
      let focalLengthToUse =
        input.focalLength || exifData.focalLength || getDefaultFocalLength()

      // If EXIF data is missing or incomplete, fill with defaults for the calibration service
      // respect session-level overrides for focal35mm
      if (input.focal35mm) {
        exifData.focalLengthIn35mm = input.focal35mm
        exifData.focal35mm = input.focal35mm
      }

      const hasF35 = exifData.focalLengthIn35mm || exifData.focal35mm
      const hasF = exifData.focalLength

      if (!hasF && !hasF35) {
        // Full fallback to saved user defaults
        exifData.focalLength = getDefaultFocalLength()
        exifData.focalLengthIn35mm = getDefaultFocal35mm()
        exifData.focal35mm = getDefaultFocal35mm()
        console.info(
          '[WeightPipeline] Using saved user defaults for both focal lengths'
        )
      } else if (hasF && !hasF35) {
        // physical focal length but no 35mm eq -> fallback to default 35mm eq
        exifData.focalLengthIn35mm = getDefaultFocal35mm()
        exifData.focal35mm = getDefaultFocal35mm()
        console.info(
          '[WeightPipeline] Using saved user default for missing 35mm equivalent'
        )
      } else if (!hasF && hasF35) {
        // 35mm eq but no physical focal length -> fallback to default physical focal length
        exifData.focalLength = getDefaultFocalLength()
        console.info(
          '[WeightPipeline] Using saved user default for missing physical focal length'
        )
      }

      const realWorldMeasurements = this.calibrationService.calibrate(
        pixelMeasurements,
        exifData,
        input.distance,
        viewingAngle,
        focalLengthToUse
      )

      // Stage 5: Weight Estimation (Apply linear regression model)
      if (progressCallback) {
        progressCallback({
          stage: 'estimation',
          progress: 0.8,
          messageKey: 'ml.pipeline.estimatingWeight',
        })
      }
      const weightEstimation = await this.runWeightEstimation(
        realWorldMeasurements
      )

      if (progressCallback) {
        progressCallback({
          stage: 'complete',
          progress: 1.0,
          messageKey: 'ml.pipeline.complete',
        })
      }

      const processingTimeMs = performance.now() - startTime

      // Determine focal length source and apply accuracy penalty
      const focalLengthSource = input.focalLength
        ? 'user-override'
        : exifData.hasFocalLength
        ? 'exif'
        : 'user-default'

      // Apply accuracy penalty for focal length source
      let finalAccuracy = weightEstimation.accuracy
      if (focalLengthSource === 'user-default') {
        finalAccuracy *= 0.9 // 10% penalty for using default focal length
      } else if (focalLengthSource === 'user-override') {
        finalAccuracy *= 0.95 // 5% penalty for user override
      }

      return {
        weight: weightEstimation.weight,
        accuracy: finalAccuracy,

        // Measurements (in cm)
        measurements: {
          bodyLength: realWorldMeasurements.lengthCm,
          shoulderHeight: realWorldMeasurements.shoulderCm,
          rumpHeight: realWorldMeasurements.rumpCm,
          bodyHeight: realWorldMeasurements.bodyCm,
          // Also provide alternative names for compatibility
          lengthCm: realWorldMeasurements.lengthCm,
          shoulderCm: realWorldMeasurements.shoulderCm,
          rumpCm: realWorldMeasurements.rumpCm,
          heightCm: realWorldMeasurements.bodyCm,
        },

        // Raw measurements
        pixelMeasurements: pixelMeasurements,
        realWorldMeasurements: realWorldMeasurements,

        // Segmentation data
        segmentation: {
          mask: segmentation.mask, // Keep as tf.Tensor2D for MeasurementService
          maskData: segmentation.maskData, // Uint8Array for visualization
          maskWidth: segmentation.maskWidth,
          maskHeight: segmentation.maskHeight,
          scaledWidth: segmentation.scaledWidth,
          scaledHeight: segmentation.scaledHeight,
          originalWidth: segmentation.originalWidth,
          originalHeight: segmentation.originalHeight,
          boundingBox: segmentation.boundingBox,
          confidence: segmentation.accuracy,
        },

        // Debug info
        debug: {
          distance: input.distance,
          angle: viewingAngle,
          direction: direction,
          exifData: exifData,
          focalLengthUsed: focalLengthToUse,
          focalLengthSource: focalLengthSource,
          calibrationParams: realWorldMeasurements.calibrationParams,
          processingTimeMs: processingTimeMs,
        },
      }
    } catch (error) {
      console.error('[WeightPipeline] Pipeline execution failed:', error)
      throw new Error(`Weight estimation failed: ${error.message}`)
    }
  }

  /**
   * Stage 1: Run segmentation
   * Based on thesis yolotfjs.ts predict()
   */
  async runSegmentation(image, imageCanvas, resultCanvas, orientation = 1) {
    try {
      return await this.segmentationService.segmentGoat(
        image,
        imageCanvas,
        resultCanvas,
        orientation
      )
    } catch (error) {
      throw new Error(`Segmentation failed: ${error.message}`)
    }
  }

  /**
   * Stage 2: Extract measurements
   * Based on thesis utils.ts bodyMeasurement()
   */
  async runMeasurement(mask, boundingBox, direction, canvas = null) {
    try {
      return await this.measurementService.extractMeasurements(
        mask,
        boundingBox,
        direction,
        canvas
      )
    } catch (error) {
      throw new Error(`Measurement extraction failed: ${error.message}`)
    }
  }

  /**
   * Stage 3: Estimate weight
   * Based on thesis weight-prediction.ts linearRegression()
   */
  async runWeightEstimation(realWorldMeasurements) {
    try {
      return await this.weightEstimatorService.estimateWeight(
        realWorldMeasurements
      )
    } catch (error) {
      throw new Error(`Weight estimation failed: ${error.message}`)
    }
  }

  /**
   * Get pipeline status
   */
  getStatus() {
    return {
      isInitialized: this.isInitialized,
      backend: this.segmentationService.getBackend(),
      models: {
        segmentation: this.segmentationService.isInitialized,
        weightEstimator: this.weightEstimatorService.getModelInfo(),
      },
    }
  }

  /**
   * Clean up all resources
   */
  async dispose() {
    await this.segmentationService.dispose()
    await this.weightEstimatorService.dispose()
    this.isInitialized = false
  }
}

/**
 * Singleton instance
 */
let pipelineInstance = null

/**
 * Get or create global pipeline instance
 * Recommended usage pattern for Vue app
 */
export function getWeightPipeline() {
  if (!pipelineInstance) {
    pipelineInstance = new WeightPipeline()
  }
  return pipelineInstance
}

/**
 * Dispose global pipeline instance
 */
export async function disposeWeightPipeline() {
  if (pipelineInstance) {
    await pipelineInstance.dispose()
    pipelineInstance = null
  }
}
